import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatINR, todayISO, API, formatDate, formatApiErrorDetail } from "../lib/api";
import { Plus, Printer, Trash2, Search, Download, Pencil, MessageCircle, Check, FileDown, Lock, Unlock } from "lucide-react";
import { RemindMeButton } from "../components/RemindMeButton";
import { generateInvoicePdf } from "../lib/pdf";
import { InvoiceFormModal } from "../components/InvoiceFormModal";
import { useAuth } from "../lib/auth";
import { usePolling } from "../lib/usePolling";
import { usePagination } from "../hooks/usePagination";
import { Pagination } from "../components/Pagination";
import { toast } from "sonner";

const blankItem = { description: "", quantity: 1, rate: 0, amount: 0 };
const blank = {
  invoice_date: todayISO(),
  due_date: "",
  invoice_number: "",
  customer_name: "", customer_address: "", customer_gst: "", customer_attn: "",
  lr_ids: [],
  items: [{ ...blankItem }],
  subtotal: 0, gst_percent: 0, gst_amount: 0, total: 0, gst_mode: "forward",
  status: "pending", notes: "",
  company_brand: "ito_engineering",
  company_id: "", department_id: "", contact_id: "",
};

export default function Invoices() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const paged = usePagination(items, 25);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [unbilled, setUnbilled] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchParams] = useSearchParams();
  const qParam = searchParams.get("q") || "";
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [q, setQ] = useState(qParam);
  useEffect(() => { setQ(qParam); }, [qParam]);
  const [company, setCompany] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [customers, setCustomers] = useState([]);

  async function load() {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (q) params.q = q;
    const { data } = await api.get("/invoices", { params });
    setItems(data);
  }
  useEffect(() => { load(); }, [statusFilter, q]); // eslint-disable-line
  usePolling(load, 8000, !showForm);
  useEffect(() => {
    api.get("/company").then(r=>setCompany(r.data)).catch(()=>{});
    api.get("/customers").then(r=>setCustomers(r.data)).catch(()=>{});
  }, []);

  function pickCustomer(name) {
    const c = customers.find((x) => x.name === name);
    setForm((f) => ({
      ...f,
      customer_name: name,
      customer_gst: c?.gst_number ?? f.customer_gst,
      customer_address: c?.address ?? f.customer_address,
    }));
  }

  async function openNew() {
    setForm(blank);
    setEditingId(null);
    const { data } = await api.get("/lrs/unbilled");
    setUnbilled(data);
    setShowForm(true);
    api.get("/invoices/next-number").then((r) => { if (r.data?.next) setForm((f) => (f.invoice_number ? f : { ...f, invoice_number: r.data.next })); }).catch(() => {});
  }

  async function openEdit(inv) {
    const { data: unbilledList } = await api.get("/lrs/unbilled");
    let attachedLRs = [];
    if (inv.lr_ids?.length) {
      const all = await Promise.all(inv.lr_ids.map((id) => api.get(`/lrs/${id}`).then(r => r.data).catch(()=>null)));
      attachedLRs = all.filter(Boolean);
    }
    setUnbilled([...unbilledList, ...attachedLRs]);
    setForm({ ...blank, ...inv });
    setEditingId(inv.id);
    setShowForm(true);
  }

  function recalc(updated) {
    const sub = (updated.items || []).reduce((s, it) => s + Number(it.amount || 0), 0);
    const gst = sub * Number(updated.gst_percent || 0) / 100;
    const rcm = updated.gst_mode === "rcm";
    return { ...updated, subtotal: +sub.toFixed(2), gst_amount: +gst.toFixed(2), total: +((rcm ? sub : sub + gst)).toFixed(2) };
  }

  function updateItem(i, k, v) {
    const items = form.items.slice();
    items[i] = { ...items[i], [k]: v };
    if (k === "quantity" || k === "rate") items[i].amount = +(Number(items[i].quantity || 0) * Number(items[i].rate || 0)).toFixed(2);
    setForm(recalc({ ...form, items }));
  }

  function addItem() { setForm(recalc({ ...form, items: [...form.items, { ...blankItem }] })); }
  function removeItem(i) { setForm(recalc({ ...form, items: form.items.filter((_, idx) => idx !== i) })); }

  function toggleLR(lr) {
    const picked = form.lr_ids.includes(lr.id);
    let lr_ids, items;
    if (picked) {
      lr_ids = form.lr_ids.filter((x) => x !== lr.id);
      items = form.items.filter((it) => it._lrId !== lr.id);
    } else {
      lr_ids = [...form.lr_ids, lr.id];
      const amt = Number(lr.quoted_amount) || Number(lr.freight_amount) || 0;
      items = [...form.items.filter(it => it.description || it.amount), {
        _lrId: lr.id,
        description: `Freight ${lr.lr_number}: ${lr.from_location} -> ${lr.to_location}`,
        quantity: 1, rate: amt, amount: amt,
      }];
    }
    let customer_name = form.customer_name;
    let customer_gst = form.customer_gst;
    let customer_address = form.customer_address;
    if (!picked && form.lr_ids.length === 0) {
      const h = lr.hirer || {};
      customer_name = h.name || lr.consignor_name;
      customer_gst = h.gst || lr.consignor_gst;
      customer_address = h.address || lr.consignor_address;
    }
    setForm(recalc({ ...form, lr_ids, items, customer_name, customer_gst, customer_address }));
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, items: form.items.map(({ _lrId, ...rest }) => rest) };
      if (editingId) {
        await api.put(`/invoices/${editingId}`, payload);
      } else {
        await api.post("/invoices", payload);
      }
      setShowForm(false);
      setForm(blank);
      setEditingId(null);
      load();
    } finally { setSaving(false); }
  }

  async function setStatus(id, status) {
    await api.patch(`/invoices/${id}/status`, { status });
    load();
  }
  async function remove(id) {
    if (!confirm("Delete invoice?")) return;
    try { await api.delete(`/invoices/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete"); }
  }

  async function approve(id) {
    if (!confirm("Approve this invoice? It will be locked for editing and can be exported to Tally.")) return;
    try { await api.post(`/invoices/${id}/approve`); toast.success("Invoice approved & locked"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not approve"); }
  }
  async function unapprove(id) {
    if (!confirm("Unapprove this invoice to unlock it for editing?")) return;
    try { await api.post(`/invoices/${id}/unapprove`); toast.success("Invoice unlocked"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not unapprove"); }
  }

  function shareWhatsApp(inv) {
    const msg = `Invoice ${inv.invoice_number || "Draft"}\nCustomer: ${inv.customer_name}\nAmount: ${formatINR(inv.total)}${inv.due_date ? `\nDue: ${formatDate(inv.due_date)}` : ""}\nStatus: ${inv.status}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`, "_blank");
  }

  return (
    <div data-testid="invoices-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Billing</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Invoices</h1>
        </div>
        <div className="flex gap-2">
          <a href={`${API}/export/invoices.csv`} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="export-invoices-csv"><Download className="w-4 h-4" /> Export CSV</a>
          <a href={`${API}/invoices/tally-export.xml`} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="export-tally-xml" title="Download all approved invoices as a TallyPrime import file"><FileDown className="w-4 h-4" /> Tally Export</a>
          <button className="btn-primary" onClick={openNew} data-testid="create-invoice-button"><Plus className="w-4 h-4" /> New Invoice</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#68665E]" />
          <input className="input-field pl-9 w-72" placeholder="Search invoice or customer..." value={q} onChange={(e)=>setQ(e.target.value)} data-testid="invoice-search" />
        </div>
        <select className="input-field w-40" value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} data-testid="invoice-status-filter">
          <option value="">All status</option>
          <option value="pending">Generated</option>
          <option value="submitted">Submitted</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <Pagination {...paged} testId="invoices-pagination" />
      <div className="table-wrap">
        <table className="w-full">
          <thead>
            <tr>
              <th className="table-th">Invoice #</th>
              <th className="table-th">Date</th>
              <th className="table-th">Customer</th>
              <th className="table-th">LRs</th>
              <th className="table-th">Total</th>
              <th className="table-th">Lifecycle</th>
              <th className="table-th">Status</th>
              <th className="table-th text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td className="table-td text-[#68665E] text-center" colSpan={8}>No invoices yet.</td></tr>
            ) : paged.pageItems.map((inv) => (
              <tr key={inv.id} className="table-row" data-testid={`invoice-row-${inv.id}`}>
                <td className="table-td font-mono text-xs">{inv.invoice_number}</td>
                <td className="table-td">{formatDate(inv.invoice_date)}</td>
                <td className="table-td"><button className="hover:underline" onClick={()=>navigate(`/entity/customer/${encodeURIComponent(inv.customer_name)}`)} data-testid={`invoice-customer-link-${inv.id}`}>{inv.customer_name}</button></td>
                <td className="table-td font-mono text-xs">{inv.lr_ids?.length || 0}</td>
                <td className="table-td font-medium">{formatINR(inv.total)}</td>
                <td className="table-td text-[11px] text-[#68665E] leading-tight" data-testid={`invoice-lifecycle-${inv.id}`}>
                  <div>Created: {formatDate(inv.created_at||inv.invoice_date) || "—"}</div>
                  <div>Submitted: {formatDate(inv.submitted_at) || "—"}</div>
                  <div>Paid: {formatDate(inv.paid_at) || "—"}</div>
                  {inv.approval_status === "approved" && (
                    <div className="text-[#2F6F4E] font-medium flex items-center gap-1 mt-0.5" data-testid={`invoice-approved-${inv.id}`}>
                      <Lock className="w-3 h-3" /> Approved: {inv.approved_by_name || "—"}{inv.approved_at ? ` · ${formatDate(inv.approved_at)}` : ""}
                    </div>
                  )}
                </td>
                <td className="table-td">
                  <select
                    className={`${inv.status === "paid" ? "badge-success" : inv.status === "overdue" || inv.status === "cancelled" ? "badge-error" : inv.status === "submitted" ? "badge-neutral" : "badge-pending"} cursor-pointer border-0 bg-transparent`}
                    value={inv.status}
                    onChange={(e) => setStatus(inv.id, e.target.value)}
                    data-testid={`invoice-status-${inv.id}`}
                    style={{ appearance: "none" }}
                  >
                    <option value="pending">Generated</option>
                    <option value="submitted">Submitted</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>
                <td className="table-td text-right">
                  {inv.approval_status === "approved" ? (
                    <>
                      <a className="btn-ghost text-[#2F6F4E] inline-flex" href={`${API}/invoices/${inv.id}/tally.xml`} target="_blank" rel="noreferrer" title="Download Tally XML" data-testid={`invoice-tally-${inv.id}`}><FileDown className="w-4 h-4" /></a>
                      {isAdmin && <button className="btn-ghost" title="Unapprove (unlock to edit)" onClick={() => unapprove(inv.id)} data-testid={`invoice-unapprove-${inv.id}`}><Unlock className="w-4 h-4" /></button>}
                      <button className="btn-ghost" onClick={() => generateInvoicePdf(inv, company)} data-testid={`invoice-print-${inv.id}`}><Printer className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <>
                      {isAdmin && <button className="btn-ghost text-[#2F6F4E]" title="Approve & lock for Tally" onClick={() => approve(inv.id)} data-testid={`invoice-approve-${inv.id}`}><Check className="w-4 h-4" /></button>}
                      <button className="btn-ghost" onClick={() => openEdit(inv)} data-testid={`invoice-edit-${inv.id}`}><Pencil className="w-4 h-4" /></button>
                      <button className="btn-ghost text-[#2F6F4E]" title="Share on WhatsApp" onClick={() => shareWhatsApp(inv)} data-testid={`invoice-whatsapp-${inv.id}`}><MessageCircle className="w-4 h-4" /></button>
                      <button className="btn-ghost" onClick={() => generateInvoicePdf(inv, company)} data-testid={`invoice-print-${inv.id}`}><Printer className="w-4 h-4" /></button>
                      {(inv.status === "overdue" || inv.status === "submitted") && <RemindMeButton entityType="invoice" entityId={inv.id} label={`Invoice ${inv.invoice_number}`} defaultTitle={`Follow up payment: ${inv.invoice_number}`} className="btn-ghost text-sm" testId={`invoice-remind-${inv.id}`} />}
                      <button className="btn-ghost text-[#8A2526]" onClick={() => remove(inv.id)} data-testid={`invoice-delete-${inv.id}`}><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <InvoiceFormModal
          form={form} setForm={setForm} recalc={recalc}
          updateItem={updateItem} addItem={addItem} removeItem={removeItem}
          toggleLR={toggleLR} pickCustomer={pickCustomer}
          unbilled={unbilled} customers={customers}
          editingId={editingId} saving={saving} save={save}
          onClose={() => { setShowForm(false); setEditingId(null); }}
        />
      )}
    </div>
  );
}
